import { useEffect, useState, useRef } from "react";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { debugLog, debugWarn, debugError } from "@/contexts/DebugContext";

interface RefSearchProgressProps {
  refNumber: string;
  manufacturer?: string;
  onComplete: (results: any[]) => void;
  onError: (error: string) => void;
  onProgressUpdate?: (progress: number) => void;
}

interface ModuleStatus {
  name: string;
  status: 'pending' | 'success' | 'failed';
  count?: number;
  duration?: number;
}

const TOTAL_MODULES = 6; // Autodoc, Oscaro, Mister Auto, Motointegrator, Distriauto, Carpardoo

const RefSearchProgress = ({ refNumber, manufacturer, onComplete, onError, onProgressUpdate }: RefSearchProgressProps) => {
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState("Recherche en cours...");
  const [failedModules, setFailedModules] = useState<string[]>([]);
  const [moduleStatuses, setModuleStatuses] = useState<ModuleStatus[]>([
    { name: 'Autodoc', status: 'pending' },
    { name: 'Oscaro', status: 'pending' },
    { name: 'Mister Auto', status: 'pending' },
    { name: 'Motointegrator', status: 'pending' },
    { name: 'Distriauto', status: 'pending' },
    { name: 'Carpardoo', status: 'pending' },
  ]);
  const evtSourceRef = useRef<EventSource | null>(null);

  useEffect(() => {
    debugLog('[RefSearchProgress] effect start', { refNumber });
    let modulesCompleted = 0;
    let allResults: any[] = [];
    let isClosing = false;
    let retryCount = 0;
    const MAX_RETRIES = 2;
    let retryTimeout: ReturnType<typeof setTimeout> | null = null;
    
    const startSearch = () => {
      debugLog('[RefSearchProgress] startSearch called', { refNumber, manufacturer });
      // Construire l'URL SSE pour la nouvelle Edge Function
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const params = new URLSearchParams({ refNumber });
      if (manufacturer) {
        params.append('manufacturer', manufacturer);
      }
      const sseUrl = `${supabaseUrl}/functions/v1/search-ref?${params.toString()}`;
      
      debugLog('[RefSearchProgress] Starting SSE connection to:', sseUrl, `(attempt ${retryCount + 1}/${MAX_RETRIES + 1})`);
      const evtSource = new EventSource(sseUrl);
      evtSourceRef.current = evtSource;

      evtSource.onmessage = (event) => {
        if (isClosing) return;
        
        try {
          const data = JSON.parse(event.data);
          debugLog('[RefSearchProgress] SSE event:', data);

          if (data.status === "moduleDone" && data.module) {
            const hasResults = Array.isArray(data.results) && data.results.length > 0;
            
            if (hasResults) {
              allResults = [...allResults, ...data.results];
            }
            
            // Update module status
            setModuleStatuses(prev => prev.map(m => 
              m.name === data.module 
                ? { ...m, status: hasResults ? 'success' : 'failed', count: data.results?.length || 0, duration: data.duration }
                : m
            ));
            
            modulesCompleted++;
            const percent = Math.min(Math.round((modulesCompleted / TOTAL_MODULES) * 100), 100);
            setProgress(percent);
            onProgressUpdate?.(percent);
            setStatusText(`Recherche en cours... ${data.module} (${percent}%)`);
          } else if (data.status === "failed" && data.module) {
            debugError(`Module ${data.module} failed:`, data.error);
            setFailedModules(prev => [...prev, data.module]);
            
            // Update module status
            setModuleStatuses(prev => prev.map(m => 
              m.name === data.module 
                ? { ...m, status: 'failed', duration: data.duration }
                : m
            ));
            
            modulesCompleted++;
            const percent = Math.min(Math.round((modulesCompleted / TOTAL_MODULES) * 100), 100);
            setProgress(percent);
            onProgressUpdate?.(percent);
          } else if (data.status === "metrics" && data.metrics) {
            // Display scraping metrics in console
            debugLog('%c📊 MÉTRIQUES DE SCRAPING', 'font-weight: bold; font-size: 14px; color: #2563eb');
            debugLog('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #2563eb');
            
            data.metrics.platforms.forEach((p: any) => {
              debugLog(`%c${p.platform}: ${p.bytesFormatted}`, 'color: #64748b');
            });
            
            debugLog('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #2563eb');
            debugLog(`%cTOTAL: ${data.metrics.total.bytesFormatted} scrappés (${data.metrics.total.platformsCount} plateformes)`, 
              'font-weight: bold; color: #2563eb');
            debugLog('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #2563eb');
          } else if (data.status === "completed") {
            debugLog('[RefSearchProgress] Received completed event, closing stream');
            isClosing = true;
            setProgress(100);
            onProgressUpdate?.(100);
            setStatusText("Recherche terminée !");
            evtSource.close();
            evtSourceRef.current = null;
            
            // Use results from completed event (for cache hits) or accumulated results
            const finalResults = data.results || allResults;
            setTimeout(() => {
              debugLog('[RefSearchProgress] Completing with results:', finalResults.length, data.cached ? '(from cache)' : '(scraped)');
              onComplete(finalResults);
            }, 300);
          }
        } catch (err) {
          debugError("[RefSearchProgress] Error parsing SSE data:", err);
        }
      };

      evtSource.onerror = (err) => {
        if (isClosing) return;
        
        debugError("[RefSearchProgress] SSE error:", err);
        evtSource.close();
        evtSourceRef.current = null;
        
        // Retry logic
        if (retryCount < MAX_RETRIES && allResults.length === 0) {
          retryCount++;
          debugLog(`[RefSearchProgress] Retrying connection (${retryCount}/${MAX_RETRIES})...`);
          setStatusText(`Reconnexion en cours... (tentative ${retryCount}/${MAX_RETRIES})`);
          retryTimeout = setTimeout(() => startSearch(), 1000);
        } else {
          isClosing = true;
          if (allResults.length > 0) {
            debugWarn('[RefSearchProgress] SSE connection lost, using partial results:', allResults.length);
            onComplete(allResults);
          } else {
            onError("Erreur lors de la connexion à la recherche. Veuillez réessayer.");
          }
        }
      };
    };
    
    startSearch();

    return () => {
      debugLog('[RefSearchProgress] Cleanup: closing EventSource');
      isClosing = true;
      if (retryTimeout) clearTimeout(retryTimeout);
      if (evtSourceRef.current) {
        evtSourceRef.current.close();
        evtSourceRef.current = null;
      }
    };
  }, [refNumber, manufacturer]);

  const handleCancel = () => {
    if (evtSourceRef.current) {
      evtSourceRef.current.close();
      evtSourceRef.current = null;
    }
    setStatusText("Recherche annulée");
    onError("Recherche annulée par l'utilisateur");
  };

  return (
    <div className="space-y-4 glass-effect p-6 rounded-lg animate-fade-in">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 text-primary animate-spin" />
          <p className="text-foreground font-medium">{statusText}</p>
        </div>
        <button
          onClick={handleCancel}
          className="px-4 py-2 text-sm font-medium text-destructive hover:text-destructive/80 transition-colors"
        >
          Annuler
        </button>
      </div>
      
      <Progress value={progress} className="h-2" />

      {/* Indicateur visuel des modules */}
      <div className="grid grid-cols-6 gap-2">
        {moduleStatuses.map((module) => (
          <div 
            key={module.name}
            className={cn(
              "text-center p-2 rounded-md text-xs transition-all",
              module.status === 'pending' && "bg-muted/50 text-muted-foreground",
              module.status === 'success' && "bg-green-500/10 text-green-600 border border-green-500/20",
              module.status === 'failed' && "bg-red-500/10 text-red-600 border border-red-500/20"
            )}
          >
            <div className="font-medium truncate">{module.name}</div>
            {module.status === 'success' && module.count !== undefined && (
              <div className="text-[10px] mt-0.5">{module.count} résultats</div>
            )}
            {module.status === 'failed' && (
              <div className="text-[10px] mt-0.5">Échec</div>
            )}
          </div>
        ))}
      </div>

      {failedModules.length > 0 && (
        <Alert className="glass-effect border-accent/50">
          <AlertDescription className="text-sm text-muted-foreground">
            Plateformes indisponibles : {failedModules.join(", ")}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default RefSearchProgress;
